import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import { Provider } from 'react-redux';
import { store } from './App/Shared/Redux/store.ts';
import { ModalProvider } from './App/Shared/Hooks/useModal.tsx';
import { UserProvider } from './App/Shared/Contexts/UserContext.tsx';
import { SchoolProvider } from './App/Shared/Contexts/SchoolContext.tsx';
import ErrorBoundary from './App/Shared/Components/ErrorBoundary.tsx';
import * as Sentry from '@sentry/react';
import { PostHogAnalytics } from './analytics.tsx';
import { installErrorToastGate } from './App/Shared/lib/errorToastGate.ts';
import { selectIsServiceUnavailable } from './App/Shared/Redux/Slice/ServiceStateSlice.ts';
import {
  safeBeforeBreadcrumb,
  safeBeforeSend,
} from './App/Shared/lib/sentryPrivacy.ts';

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: 'https://1f16b5c618bd7a1fbe8772721cbda1eb@o1200326.ingest.us.sentry.io/4508412982788096',
    environment: 'production',
    // Load-bearing: this depth collapses nested request/response bodies to
    // "[Object]" in anything the SDK serialises. Raising it is a deliberate,
    // reviewable act -- it widens what tenant data any unscrubbed field exposes.
    // (extraErrorDataIntegration is deliberately not enabled: it copies thrown
    // objects' fields into the event.)
    normalizeDepth: 3,
    // Fail-closed: strips bodies, query values, tokens and console output, and
    // keeps digital-receipt secrets out (see sentryPrivacy.ts, receiptPrivacy.ts).
    beforeSend: safeBeforeSend,
    beforeBreadcrumb: safeBeforeBreadcrumb,
  });
}

installErrorToastGate(() => selectIsServiceUnavailable(store.getState()));

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <Provider store={store}>
        <ModalProvider>
          <BrowserRouter>
            <PostHogAnalytics>
              <UserProvider>
                <SchoolProvider>
                  <App />
                </SchoolProvider>
              </UserProvider>
            </PostHogAnalytics>
          </BrowserRouter>
        </ModalProvider>
      </Provider>
    </ErrorBoundary>
  </StrictMode>
);
